import { useMemo } from "react";
import { Calendar, dateFnsLocalizer, Views, type View } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, addDays } from "date-fns";
import { enUS } from "date-fns/locale";
import { useAppStore } from "@/stores/appStore";
import { CalendarEvent } from "./CalendarEvent";
import type { Initiative } from "@/types";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendar-theme.css";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { locale: enUS }),
  getDay,
  locales: { "en-US": enUS },
});

// Standard calendar quarters (matches the Timeline superheader): Q1=Jan/Feb/Mar Q2=Apr/May/Jun
// Q3=Jul/Aug/Sep Q4=Oct/Nov/Dec
const QUARTER_GROUPS = [
  { label: "Q1", months: ["Jan", "Feb", "Mar"] },
  { label: "Q2", months: ["Apr", "May", "Jun"] },
  { label: "Q3", months: ["Jul", "Aug", "Sep"] },
  { label: "Q4", months: ["Oct", "Nov", "Dec"] },
];

// Parse a stored ISO datetime as a LOCAL calendar date (midnight local). Stored schedule dates are
// UTC midnight (e.g. "2026-11-01T00:00:00.000Z"); parsing with `new Date()` shifts them a day
// earlier in timezones west of UTC. Taking the YYYY-MM-DD portion keeps the intended calendar day.
function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

// Quarter-group label (e.g. "2026 Q4") for whichever month `date` falls in — purely a decorative
// subtitle under the toolbar's month label, so it degrades gracefully for non-"FY"-style prefixes.
function quarterGroupLabel(
  date: Date,
  quarters: { fullLabel: string; startDate: string; endDate: string }[]
): string | null {
  const q = quarters.find((qq) => {
    const s = parseLocalDate(qq.startDate);
    const e = parseLocalDate(qq.endDate);
    return date >= s && date <= e;
  });
  if (!q) return null;
  const parts = q.fullLabel.split(" ");
  const prefix = parts[0];
  const monthAbbr = (parts[1] || "").slice(0, 3);
  const grp = QUARTER_GROUPS.find((g) => g.months.includes(monthAbbr))?.label;
  return grp ? `${prefix} ${grp}` : prefix;
}

interface CalendarEventItem {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: true;
  resource: Initiative;
}

interface CalendarViewProps {
  initiatives?: Initiative[];
  view: View;
  onViewChange: (v: View) => void;
  date: Date;
  onNavigate: (d: Date) => void;
}

export function CalendarView({ initiatives: filtered, view, onViewChange, date, onNavigate }: CalendarViewProps) {
  const { initiatives: allInitiatives, quarters, openViewInitiativeModal } = useAppStore();
  const initiatives = filtered || allInitiatives;

  // Fallback: derive a Date from the initiative's mapped quarter when exact dates are missing
  // (legacy rows are backfilled, but this keeps the calendar robust if any slip through).
  const quarterStart = (key?: string): Date | null => {
    if (!key) return null;
    const q = quarters.find((x: any) => x.key === key);
    return q ? parseLocalDate(q.startDate) : null;
  };
  const quarterEnd = (key?: string): Date | null => {
    if (!key) return null;
    const q = quarters.find((x: any) => x.key === key);
    return q ? parseLocalDate(q.endDate) : null;
  };

  const events = useMemo<CalendarEventItem[]>(() => {
    return initiatives
      .map((init: any) => {
        const start = init.startDate ? parseLocalDate(init.startDate) : quarterStart(init.startQuarter);
        if (!start) return null;
        const rawEnd = init.endDate
          ? parseLocalDate(init.endDate)
          : quarterEnd(init.endQuarter) || start;
        // All-day events treat `end` as exclusive — add a day so the bar spans through the end date.
        const end = addDays(rawEnd < start ? start : rawEnd, 1);
        return {
          id: init.id,
          title: init.title,
          start,
          end,
          allDay: true as const,
          resource: init,
        };
      })
      .filter((e: CalendarEventItem | null): e is CalendarEventItem => e !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiatives, quarters]);

  // Custom toolbar — mirrors the default (Today/Back/Next + view buttons) but adds a muted
  // quarter-group subtitle under the month/period label. Memoized on `quarters` so RBC doesn't
  // remount it.
  const Toolbar = useMemo(() => {
    return function CalendarToolbar({ label, date: toolbarDate, onNavigate, onView, view: currentView }: any) {
      const qg = quarterGroupLabel(toolbarDate as Date, quarters);
      return (
        <div className="rbc-toolbar">
          <span className="rbc-btn-group">
            <button type="button" onClick={() => onNavigate("TODAY")}>Today</button>
            <button type="button" onClick={() => onNavigate("PREV")}>Back</button>
            <button type="button" onClick={() => onNavigate("NEXT")}>Next</button>
          </span>
          <span className="rbc-toolbar-label flex flex-col items-center leading-tight">
            <span>{label}</span>
            {qg && <span className="text-[11px] font-normal text-text-tertiary">{qg}</span>}
          </span>
          <span className="rbc-btn-group">
            <button type="button" className={currentView === "month" ? "rbc-active" : ""} onClick={() => onView("month")}>Month</button>
            <button type="button" className={currentView === "week" ? "rbc-active" : ""} onClick={() => onView("week")}>Week</button>
            <button type="button" className={currentView === "agenda" ? "rbc-active" : ""} onClick={() => onView("agenda")}>Agenda</button>
          </span>
        </div>
      );
    };
  }, [quarters]);

  return (
    <div className="h-full px-4 pb-4">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        allDayAccessor="allDay"
        defaultView={Views.MONTH}
        view={view}
        onView={onViewChange}
        date={date}
        onNavigate={onNavigate}
        views={[Views.MONTH, Views.WEEK, Views.AGENDA]}
        popup
        components={{ event: CalendarEvent, toolbar: Toolbar }}
        onSelectEvent={(e) => openViewInitiativeModal((e as CalendarEventItem).resource)}
        eventPropGetter={() => ({
          // Neutralize react-big-calendar's default event chrome so our pill controls the look.
          style: { backgroundColor: "transparent", border: "none", padding: 0, margin: "1px 0" },
        })}
        style={{ height: "100%" }}
      />
    </div>
  );
}
